import React, { useState } from "react";
import * as Yup from "yup";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import Button from "../../ui/Button";
import ModalInput from "../../ui/ModalTextInput";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { toast } from "react-toastify";
import Dropdown from "../../ui/SelectDropdown";
import { completeCourierPaymentAsync } from "../../../redux/features/orderSlice";

// Form data interface
interface UpdateCourierFeeFormData {
  paymentStatus: string;
}

// Props interface
interface UpdateCourierFeeStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: any;
  onSave: () => void;
}

// Validation schema
const validationSchema = Yup.object({
  paymentStatus: Yup.string().required("Payment status is required"),
});

const UpdateCourierFeeStatusModal: React.FC<
  UpdateCourierFeeStatusModalProps
> = ({ isOpen, onClose, order, onSave }) => {
  const { loading } = useAppSelector((state) => state.orders);
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<UpdateCourierFeeFormData>({
    paymentStatus: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof UpdateCourierFeeFormData, string>>
  >({});

  const handleSelectChange = (
    name: keyof UpdateCourierFeeFormData,
    value: any
  ) => {
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationErrors: any) {
      const validationResult: Partial<
        Record<keyof UpdateCourierFeeFormData, string>
      > = {};
      validationErrors.inner.forEach((err: any) => {
        validationResult[err.path as keyof UpdateCourierFeeFormData] =
          err.message;
      });
      setErrors(validationResult);
      return false;
    }
  };

  const handleSave = async () => {
    const isValid = await validateForm();
    if (isValid) {
      try {
        await dispatch(
          completeCourierPaymentAsync({
            orderId: order?.id,
            paymentStatus: formData.paymentStatus,
          })
        ).unwrap();
        toast.success("Payment status updated successfully");
        onClose();
        onSave();
      } catch (err: any) {
        toast.error(
          err?.message ?? "Failed to update payment status. Please try again."
        );
      }
    }
  };

  if (!isOpen) return null;

  return (
    <CustomModalContainer
      title="Update Courier Fee Payment Status"
      footer={
        <div className="flex justify-between">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            customStyle={{
              width: "auto",
              minWidth: "150px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            label="Save Changes"
            type="button"
            onClick={handleSave}
            customStyle={{
              width: "auto",
              minWidth: "150px",
            }}
            isLoading={loading}
            disabled={
              Number(order?.actualCourierFee) -
                Number(order?.estimatedCourierFee) <
              0
            }
          />
        </div>
      }
      open={isOpen}
      onClose={onClose}
    >
      <div className="p-6">
        <form>
          {/* Order ID (Read-only display) */}
          <ModalInput
            label="Order ID"
            name="orderId"
            placeholder="Order ID"
            value={order?.id}
            required
            disabled
          />

          <ModalInput
            value={
              Number(order?.actualCourierFee) -
              Number(order?.estimatedCourierFee)
            }
            error={
              Number(order?.actualCourierFee) -
                Number(order?.estimatedCourierFee) <
                0 &&
              "This fee cannot be transitioned to Paid because the value is negative"
            }
            disabled
          />

          {/* Payment Status Dropdown */}
          <div className="mb-4">
            <Dropdown
              label="Payment Status"
              options={["PAID"]}
              value={formData.paymentStatus}
              onSelect={(value: any) =>
                handleSelectChange("paymentStatus", value)
              }
              error={errors.paymentStatus}
              placeholder="Select status"
              required
            />
          </div>
        </form>
      </div>
    </CustomModalContainer>
  );
};

export default UpdateCourierFeeStatusModal;
